import { Shape } from '../types/Shape';

// 객체의 주요 정렬 포인트 (모서리, 중앙 등)
export interface AlignmentPoint {
  x: number;
  y: number;
  type: 'left' | 'right' | 'top' | 'bottom' | 'center' | 'centerX' | 'centerY';
}

// 정렬 가이드라인 타입
export interface AlignmentGuide {
  type: 'vertical' | 'horizontal';
  position: number; // x 또는 y 좌표 (캔버스 좌표)
}

// 객체의 정렬 포인트 계산 (모서리, 중앙 등)
export const getAlignmentPoints = (shape: Shape): AlignmentPoint[] => {
  if (shape.type === 'arrow') {
    // 화살표는 스냅 대상에서 제외
    return [];
  }

  const w = shape.width || 0;
  const h = shape.height || 0;
  
  if (w === 0 || h === 0) return [];
  
  if (shape.type === 'rect' || shape.type === 'pdf' || shape.type === 'image' || 
      shape.type === 'code' || shape.type === 'md' || shape.type === 'youtube') {
    return [
      { x: shape.x, y: shape.y, type: 'left' }, // 좌상단
      { x: shape.x + w / 2, y: shape.y, type: 'top' }, // 상단 중앙
      { x: shape.x + w, y: shape.y, type: 'right' }, // 우상단
      { x: shape.x, y: shape.y + h / 2, type: 'left' }, // 좌측 중앙
      { x: shape.x + w / 2, y: shape.y + h / 2, type: 'center' }, // 중앙
      { x: shape.x + w, y: shape.y + h / 2, type: 'right' }, // 우측 중앙
      { x: shape.x, y: shape.y + h, type: 'left' }, // 좌하단
      { x: shape.x + w / 2, y: shape.y + h, type: 'bottom' }, // 하단 중앙
      { x: shape.x + w, y: shape.y + h, type: 'right' }, // 우하단
    ];
  } else if (shape.type === 'circle') {
    const centerX = shape.x + w / 2;
    const centerY = shape.y + h / 2;
    return [
      { x: shape.x, y: shape.y, type: 'left' }, // 좌상단
      { x: centerX, y: shape.y, type: 'top' }, // 상단 중앙
      { x: shape.x + w, y: shape.y, type: 'right' }, // 우상단
      { x: shape.x, y: centerY, type: 'left' }, // 좌측 중앙
      { x: centerX, y: centerY, type: 'center' }, // 중앙
      { x: shape.x + w, y: centerY, type: 'right' }, // 우측 중앙
      { x: shape.x, y: shape.y + h, type: 'left' }, // 좌하단
      { x: centerX, y: shape.y + h, type: 'bottom' }, // 하단 중앙
      { x: shape.x + w, y: shape.y + h, type: 'right' }, // 우하단
    ];
  }
  
  return [];
};

// 드래그 중인 객체의 위치를 스냅 가능한 위치로 조정
export const calculateSnapPosition = (
  draggedShape: Shape,
  targetX: number,
  targetY: number,
  allShapes: Shape[],
  snapThreshold: number = 10 // 픽셀 단위 스냅 임계값
): {
  snappedX: number;
  snappedY: number;
  guides: AlignmentGuide[];
} => {
  let snappedX = targetX;
  let snappedY = targetY;
  const guides: AlignmentGuide[] = [];
  
  // 드래그 중인 객체를 제외한 다른 객체들
  const otherShapes = allShapes.filter(s => s.id !== draggedShape.id && s.type !== 'arrow');
  
  if (otherShapes.length === 0) {
    return { snappedX, snappedY, guides };
  }
  
  // 드래그 중인 객체의 정렬 포인트 (새 위치 기준)
  const draggedShapeWithNewPos: Shape = {
    ...draggedShape,
    x: targetX,
    y: targetY,
  };
  const draggedPoints = getAlignmentPoints(draggedShapeWithNewPos);
  
  if (draggedPoints.length === 0) {
    return { snappedX, snappedY, guides };
  }
  
  // 모든 다른 객체의 정렬 포인트 수집 (중복 제거로 최적화)
  const allAlignmentPointsSet = new Set<number>(); // X 좌표들 (Set으로 중복 제거)
  const allAlignmentYPointsSet = new Set<number>(); // Y 좌표들 (Set으로 중복 제거)
  
  otherShapes.forEach(shape => {
    const points = getAlignmentPoints(shape);
    points.forEach(point => {
      allAlignmentPointsSet.add(point.x);
      allAlignmentYPointsSet.add(point.y);
    });
  });
  
  // Set을 배열로 변환 (정렬해서 검색 최적화)
  const allAlignmentPoints = Array.from(allAlignmentPointsSet).sort((a, b) => a - b);
  const allAlignmentYPoints = Array.from(allAlignmentYPointsSet).sort((a, b) => a - b);
  
  // X축 스냅 검사 - 드래그 중인 객체의 모든 X 좌표와 비교
  const draggedXPoints: number[] = [];
  draggedPoints.forEach(point => {
    draggedXPoints.push(point.x);
  });
  
  let minXDistance = snapThreshold;
  let bestXSnap: number | null = null;
  
  draggedXPoints.forEach(draggedX => {
    allAlignmentPoints.forEach(alignX => {
      const distance = Math.abs(draggedX - alignX);
      if (distance < minXDistance) {
        minXDistance = distance;
        bestXSnap = alignX;
      }
    });
  });
  
  // Y축 스냅 검사 - 드래그 중인 객체의 모든 Y 좌표와 비교
  const draggedYPoints: number[] = [];
  draggedPoints.forEach(point => {
    draggedYPoints.push(point.y);
  });
  
  let minYDistance = snapThreshold;
  let bestYSnap: number | null = null;
  
  draggedYPoints.forEach(draggedY => {
    allAlignmentYPoints.forEach(alignY => {
      const distance = Math.abs(draggedY - alignY);
      if (distance < minYDistance) {
        minYDistance = distance;
        bestYSnap = alignY;
      }
    });
  });
  
  // X축 스냅 적용
  if (bestXSnap !== null) {
    // 어떤 드래그 포인트가 스냅되었는지 찾기
    let matchedDraggedX = draggedXPoints[0];
    for (const draggedX of draggedXPoints) {
      if (Math.abs(draggedX - bestXSnap!) < snapThreshold) {
        matchedDraggedX = draggedX;
        break;
      }
    }
    const offsetX = bestXSnap - matchedDraggedX;
    snappedX = targetX + offsetX;
    
    guides.push({
      type: 'vertical',
      position: bestXSnap,
    });
  }
  
  // Y축 스냅 적용
  if (bestYSnap !== null) {
    // 어떤 드래그 포인트가 스냅되었는지 찾기
    let matchedDraggedY = draggedYPoints[0];
    for (const draggedY of draggedYPoints) {
      if (Math.abs(draggedY - bestYSnap!) < snapThreshold) {
        matchedDraggedY = draggedY;
        break;
      }
    }
    const offsetY = bestYSnap - matchedDraggedY;
    snappedY = targetY + offsetY;
    
    guides.push({
      type: 'horizontal',
      position: bestYSnap,
    });
  }
  
  return { snappedX, snappedY, guides };
};

